import { PipelineSave, ITopology, useGetConfigQuery, useSaveStreamMutation, RuntimeMode } from '../app/apiSlice';
import { NodeDefinitions, setChangedTopology, setTopologyData, setTopology } from '../pages/editor/editorSlice';
import { useAppDispatch, useAppSelector } from './hooks';
import { DagItem, useGraph } from './useGraph';
import { setToast } from '../app/appSlice';
import { useRedirect404 } from './useRedirect404';

export const useSavePipeline = () => {
    const { render, save } = useGraph();
    const [ saveStream, saveStatus ] = useSaveStreamMutation();
    useRedirect404(saveStatus.error,"treatments")
    const dispatch = useAppDispatch();
    const app = useAppSelector((state) => state.editor);
    const topologyData = useAppSelector((state) => state.editor.topologyData);
    const { data : config } = useGetConfigQuery();

    const getPipelineToSave = (topology?: ITopology, catalog?: NodeDefinitions): PipelineSave => {
        if (!topology) {
            throw Error('Can\'t get data to save pipeline without `topology`');
        }

        let flowRender = undefined;
        if (catalog) flowRender = render({
            hidePassword: false,
            catalog: catalog
        });

        if (!flowRender) {
            throw Error('Error while getting graph');
        }

        // Add pause/update values from local topology data
        flowRender = flowRender.map((brick: DagItem) => {
          const dataFromSlice = topologyData.find((b) => b.id === brick.id);
          if (dataFromSlice && topology.runtimeMode === RuntimeMode.BASIC) {
            const pause = dataFromSlice.meta?.pause;
            const update = dataFromSlice.meta?.update;
            brick.meta = { ...brick.meta, pause, update };
          }
          if (brick.type == "DELETED-BOX") {
            throw Error('Cannot save with DELETED-BOX');
          }
          return brick;
        });

        let listeZipFormatted = "";
        if (config && config.onlyMyUdf) {
            const listeZip: string[] = [
                ...new Set(flowRender
                    .filter(node => node?.code)
                    .map(node => node.code as string)
                )
            ];
            listeZipFormatted = listeZip.length > 1 ? listeZip.join(', ') : listeZip[0] || "";

        }

        return {
            id: topology.id,
            parallelism: app.current?.parallelism ?? topology.parallelism,
            // Use the name and description of topology stored in redux because if these variable are changed, the news variables are up to date only in the store redux
            name: app.current?.name ?? topology.name,
            description: app.current?.description ?? topology.description,
            data: JSON.stringify(flowRender),
            graph: save(),
            environnement: app.current?.environnement ?? topology.environnement,
            cpuLimit: app.current?.cpuLimit ?? topology.cpuLimit,
            cpuRequest: app.current?.cpuRequest ?? topology.cpuRequest,
            memoryLimit: app.current?.memoryLimit ?? topology.memoryLimit,
            memoryRequest: app.current?.memoryRequest ?? topology.memoryRequest,
            runtimeMode: app.current?.runtimeMode ?? topology.runtimeMode ,
            runtimeVersion: app.current?.runtimeVersion ?? topology.runtimeVersion,
            schedule : app.current?.schedule  ||  app.current?.schedule === "" ? app.current.schedule : topology.schedule,
            metadatas: app.current?.metadatas ?? topology.metadatas,
            forcedflowname : app.current?.forcedflowname ?? "",
            modeBatch: app.current?.modeBatch ?? topology.modeBatch,
            listeZip: listeZipFormatted,
            messageStore: app.current?.messageStore ?? topology.messageStore,
            retentionTimeNats: app.current?.retentionTimeNats ?? topology.retentionTimeNats,
            globalConfigAdditions: app.current?.globalConfigAdditions ?? topology.globalConfigAdditions,
        };
    }

    const savePipeline = async (topology?: ITopology, catalog?: NodeDefinitions, versionName?: string, createVersion: boolean = true) => {
        try {
          const pipSave = getPipelineToSave(topology, catalog);
          if (pipSave) {
            const newTopology = await saveStream({pipSave, versionName: versionName ?? topology!.version, createVersion}).unwrap();
            dispatch(setTopology(newTopology));
            dispatch(setTopologyData(newTopology.data));
            dispatch(
              setToast({
                severity: 'success',
                content: `Sauvegarde effectuée avec succès`,
                life: 1000,
              })
            );
            dispatch(setChangedTopology(false));
          }
        } catch (err: any) {
          const rawMessage =
            err?.message ||
            err?.data?.message ||
            err?.toString() ||
            "Erreur inconnue";

          const errorSnippet = rawMessage.substring(0, 100);

          dispatch(
            setToast({
              severity: 'error',
              content: `Echec de la sauvegarde : ${errorSnippet}`,
              life: 1000,
            })
          );
        }
      };

    return { savePipeline, saveStatus };
};